import type { AuthUser } from '@stayon/auth-core';
import { settingsNavigation } from '@/features/settings/settingsNavigation';
import type { AreaHeaderModel, AreaSwitchItem } from '@/shared/navigation';
import type { NavEntry, NavSearch } from './adminNavigation';
import {
  activeEntryIn,
  visibleAccountEntries,
  visiblePlatformEntries,
  type ActivePlace,
} from './navVisibility';
import type { NavCounts } from './useNavCounts';

/**
 * Reiter einer Seite, die mehrere Unterseiten hat. Die Preisautomatik verlor mit der neuen
 * Seitenleiste ihre aufklappbare Gruppe; ihre Seiten sind jetzt Reiter im Bereichskopf. Der Kalender
 * ging in Verfügbarkeit & Vertrieb auf.
 */
const PAGE_TABS: Record<string, NavEntry[]> = {
  '/dynamic-pricing': [
    { to: '/dynamic-pricing', label: 'Übersicht' },
    { to: '/dynamic-pricing/configuration', label: 'Einstellungen' },
    { to: '/dynamic-pricing/history', label: 'Verlauf' },
  ],
  '/system/audit': [
    { to: '/system/audit', label: 'Änderungsprotokoll' },
    { to: '/system/outbox', label: 'Mail-Ausgang' },
    { to: '/system/webhook-events', label: 'Meldungen des Kanalmanagers' },
  ],
};

/** Reiter-Ziele einer Seite; leer, wenn die Seite keine Reiter hat. */
export const pageTabsFor = (entry: NavEntry): NavEntry[] => PAGE_TABS[entry.to] ?? [];

/** Seiten ohne Platz in der Leiste, die trotzdem einen Pfad brauchen. */
const LOOSE_PAGES: Record<string, string> = { '/search/advanced': 'Erweiterte Suche' };

const link = (entry: NavEntry) => ({ label: entry.label, to: entry.to, search: entry.search });

/** Steht man auf genau dieser Seite (nicht auf einer Unterseite oder einem Arbeitsraum darunter)? */
function isExactly(entry: NavEntry, path: string, search: NavSearch): boolean {
  if (path !== entry.to) return false;
  return Object.entries(entry.search ?? {}).every(([k, v]) => String(search[k] ?? '') === v);
}
const here = (entry: NavEntry, path: string, search: NavSearch) => ({
  ...link(entry),
  current: isExactly(entry, path, search),
});

function items(entries: NavEntry[], active: NavEntry | null, counts: NavCounts): AreaSwitchItem[] {
  return entries.map((entry) => ({
    ...link(entry),
    active: entry === active,
    count: entry.count ? counts[entry.count] : undefined,
  }));
}

function tabsFor(entry: NavEntry, path: string, search: NavSearch, counts: NavCounts) {
  const tabs = PAGE_TABS[entry.to];
  return tabs ? items(tabs, activeEntryIn(tabs, path, search), counts) : [];
}

function settingsItem(path: string): { label: string; to: string } | null {
  for (const group of settingsNavigation) {
    const item = group.items.find((i) => path === i.to || path.startsWith(`${i.to}/`));
    if (item) return item;
  }
  return null;
}

/** Pfad, Bereichsumschalter und Reiter für die aktuelle Adresse. */
export function buildAreaHeader(
  user: AuthUser | null,
  place: ActivePlace,
  path: string,
  search: NavSearch,
  counts: NavCounts,
): AreaHeaderModel {
  if (place?.kind === 'area') {
    const { area, entry } = place;
    const first = area.entries[0] as NavEntry;
    const crumbs =
      area.entries.length > 1
        ? [{ label: area.label, to: first.to, search: first.search }, here(entry, path, search)]
        : [here(entry, path, search)];
    // Ein Reiter unter der Seite (Preisautomatik › Einstellungen) ist der dritte Teil des Pfads.
    const tab = activeEntryIn(pageTabsFor(entry), path, search);
    if (tab && tab.to !== entry.to) crumbs.push(here(tab, path, search));
    const switcher = area.entries.length > 1 ? items(area.entries, entry, counts) : [];
    return { crumbs, switcher, tabs: tabsFor(entry, path, search, counts) };
  }
  if (place?.kind === 'platform') {
    const entries = visiblePlatformEntries(user);
    return {
      crumbs: [
        { label: 'Plattform', to: entries[0]?.to ?? place.entry.to },
        here(place.entry, path, search),
      ],
      switcher: items(entries, place.entry, counts),
      tabs: tabsFor(place.entry, path, search, counts),
    };
  }
  if (place?.kind === 'account') {
    const entries = visibleAccountEntries(user);
    return {
      crumbs: [
        { label: 'Konto', to: entries[0]?.to ?? place.entry.to },
        here(place.entry, path, search),
      ],
      switcher: items(entries, place.entry, counts),
      tabs: [],
    };
  }
  if (place?.kind === 'settings') {
    const item = settingsItem(path);
    const hub = { label: 'Einstellungen', to: '/settings', current: path === '/settings' };
    const crumbs = item
      ? [hub, { label: item.label, to: item.to, current: path === item.to }]
      : [hub];
    return { crumbs, switcher: [], tabs: [] };
  }
  const loose = LOOSE_PAGES[path];
  return {
    crumbs: loose ? [{ label: loose, to: path, current: true }] : [],
    switcher: [],
    tabs: [],
  };
}
